/**
 * Settings API service for company profile and preferences.
 */

import { apiRequest } from '../utils/apiClient';
import type { CompanyProfile, CompanySettings } from '../types/company';

/**
 * Decode HTML entities in a string.
 * Fixes double/triple-encoded entities like &amp;amp; -> &
 *
 * SECURITY (security-guidance): uses DOMParser, whose parsed document is
 * INERT — no scripts run, no subresources (img/…) load — unlike assigning
 * server-provided strings to a live element's innerHTML, which is an XSS
 * gadget. Never switch this back to the textarea.innerHTML trick.
 */
function decodeHtmlEntities(str: string | null | undefined): string | undefined {
  if (str === null || str === undefined) return undefined;

  const parser = new DOMParser();
  // Decode repeatedly to handle nested encoding (&amp;amp; → &amp; → &).
  let decoded = str;
  let prev = '';
  while (decoded !== prev) {
    prev = decoded;
    decoded = parser.parseFromString(decoded, 'text/html').documentElement.textContent ?? decoded;
  }
  return decoded;
}

/**
 * Decode HTML entities in profile string fields.
 */
function decodeProfileData(data: CompanyProfile): CompanyProfile {
  return {
    ...data,
    name: decodeHtmlEntities(data.name) ?? data.name,
    nameAr: decodeHtmlEntities(data.nameAr),
    email: decodeHtmlEntities(data.email),
    phone: decodeHtmlEntities(data.phone),
    address: decodeHtmlEntities(data.address),
    description: decodeHtmlEntities(data.description),
    descriptionAr: decodeHtmlEntities(data.descriptionAr),
    website: decodeHtmlEntities(data.website),
  };
}

/**
 * Sanitize profile data for API submission.
 * Converts null values to undefined (backend schema requires string | undefined, not null).
 */
function sanitizeProfileData(data: Partial<CompanyProfile>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(data)) {
    // Skip null values (they become undefined/omitted)
    if (value !== null) {
      sanitized[key] = value;
    }
  }
  return sanitized;
}

/**
 * Fetch company profile data.
 * Endpoint: GET /companies/my/profile
 */
export async function fetchCompanyProfile(token: string): Promise<CompanyProfile> {
  const data = await apiRequest<CompanyProfile>('/companies/my/profile', {
    method: 'GET',
    token,
  });
  // Decode any HTML entities in string fields
  return decodeProfileData(data);
}

/**
 * Update company profile.
 * Endpoint: PATCH /companies/my/profile
 */
export async function updateCompanyProfile(
  token: string,
  data: Partial<CompanyProfile>
): Promise<CompanyProfile> {
  const result = await apiRequest<CompanyProfile>('/companies/my/profile', {
    method: 'PATCH',
    token,
    body: sanitizeProfileData(data),
  });
  // Decode any HTML entities in the returned data
  return decodeProfileData(result);
}

/**
 * Upload company logo.
 * Endpoint: POST /images/upload
 * Returns the uploaded image URL.
 */
export async function uploadCompanyLogo(
  token: string,
  file: File
): Promise<{ url: string }> {
  const formData = new FormData();
  formData.append('file', file);

  // Use apiRequest which handles the base URL correctly
  const data = await apiRequest<{
    secure_url?: string;
    url: string;
  }>('/images/upload?entity_type=company', {
    method: 'POST',
    token,
    body: formData,
  });

  return { url: data.secure_url || data.url };
}

/**
 * Remove company logo.
 * Endpoint: DELETE /companies/my/logo
 */
export async function removeCompanyLogo(token: string): Promise<void> {
  await apiRequest('/companies/my/logo', {
    method: 'DELETE',
    token,
  });
}

/**
 * Update company settings (preferences).
 * Endpoint: PATCH /companies/my/settings
 */
export async function updateCompanySettings(
  token: string,
  data: Partial<CompanySettings>
): Promise<CompanySettings> {
  return apiRequest<CompanySettings>('/companies/my/settings', {
    method: 'PATCH',
    token,
    body: data,
  });
}

// ============================================================================
// PUBLIC SYSTEM SETTINGS (no auth required)
// ============================================================================

/** Default SAR value of 1 coin, used as a fallback when the setting is absent. */
export const DEFAULT_COIN_RATE = 5;

/**
 * Fetch the public settings map ({ key: value }). Keys include `coin_sar_rate`.
 * Endpoint: GET /admin/settings/public (no token required).
 */
export async function getPublicSettings(): Promise<Record<string, unknown>> {
  const response = await apiRequest<{ data: Record<string, unknown> }>(
    '/admin/settings/public',
    { method: 'GET' },
  );
  return response.data;
}

/**
 * Resolve the current SAR-per-coin rate from public settings.
 * Falls back to {@link DEFAULT_COIN_RATE} when the setting is missing/malformed.
 */
export async function getCoinRate(): Promise<number> {
  try {
    const settings = await getPublicSettings();
    const raw = settings['coin_sar_rate'];
    if (raw && typeof raw === 'object' && 'sarPerCoin' in raw) {
      const value = Number((raw as { sarPerCoin?: unknown }).sarPerCoin);
      if (Number.isFinite(value) && value > 0) {
        return value;
      }
    }
  } catch {
    // Public settings unavailable — fall through to the default.
  }
  return DEFAULT_COIN_RATE;
}
